import Cookies from "js-cookie";
import * as APITypes from "~/API";
import {
  getUserInfoWithLambda,
  type FirestoreUserProfile,
} from "~/services/server/user-tenant/getUserInfoWithLambda";
import { LoaderData, AuthAccessInfo } from "~/types";
import { clearCachedIdToken } from "~/services/apiClient";
import {
  getAuthContextCache,
  invalidateAuthContextCache,
  setAuthContextCache,
} from "./authContextCache";
import { waitForAuthReady } from "./waitForAuthReady";

/** Result of loadCookiesData; includes profile when loader/user was already fetched. */
export type LoadCookiesDataResult = LoaderData<AuthAccessInfo> & {
  profile: FirestoreUserProfile | null;
  /** True when `getUserInfoWithLambda` ran inside loadCookiesData (cookie refresh path). */
  didFetchUserLoader: boolean;
};

/**
 * Test-only override: when set, loadCookiesData returns this immediately.
 * Use in integration tests to simulate different owner/role contexts without spies.
 * Call setTestOverride in beforeAll, clearTestOverride in afterAll.
 */
let __testOverride: AuthAccessInfo | null = null;

export function setTestOverride(data: AuthAccessInfo): void {
  __testOverride = data;
}

export function clearTestOverride(): void {
  __testOverride = null;
}

/**
 * Loads and validates authentication and access information from cookies.
 * If cookies are missing or invalid, fetches user info from the database and updates cookies.
 * In test mode, automatically returns mock data from environment variables.
 *
 * @param userID - The ID of the current user
 * @returns LoaderData containing the user's language preference, ownerID, ownerType, and role
 */
export const loadCookiesData = async (
  userID: string
): Promise<LoadCookiesDataResult> => {
  if (__testOverride) {
    return {
      success: true,
      data: __testOverride,
      profile: null,
      didFetchUserLoader: false,
    };
  }

  const cached = getAuthContextCache(userID);
  if (cached) {
    return cached;
  }

  // Wait for Firebase to restore persisted auth before loaders read currentUser.
  if (typeof window !== "undefined") {
    await waitForAuthReady();
  }

  // Check if we're in test/staging mode
  const isTestMode = process.env.REACT_APP_TESTING_MODE === "true";

  // If in test mode, return mock data from environment variables
  if (isTestMode) {
    const ownerId =
      process.env.REACT_APP_TESTING_OWNER_ID ||
      process.env.REACT_APP_TESTING_WORKER_USER_ID ||
      userID ||
      "";
    const ownerTypeStr = process.env.REACT_APP_TESTING_OWNER_TYPE || "User";
    const ownerType =
      ownerTypeStr === "Organization"
        ? APITypes.OwnerType.Organization
        : APITypes.OwnerType.User;

    const testResult = {
      success: true as const,
      data: {
        ownerID: ownerId,
        ownerType,
        role: APITypes.ManagerRolesType.Admin,
      },
      profile: null,
      didFetchUserLoader: false,
    };
    setAuthContextCache(userID, testResult);
    return testResult;
  }

  // Get authentication and access data from cookies
  let ownerID = Cookies.get("ownerID");
  let ownerType = Cookies.get("ownerType") as APITypes.OwnerType;
  let role = Cookies.get("role") as APITypes.ManagerRolesType;

  /**
   * Always resolve owner from the User table on cache miss. Stale Organization
   * cookies (left after leaving an org, or after org-staff tests) would otherwise
   * send ownerType=Organization into Lambda and 403 report create.
   *
   * Cookies are only a fallback when the user loader fails.
   */
  const isOwnerTypeInvalid =
    (ownerType !== APITypes.OwnerType.User &&
      ownerType !== APITypes.OwnerType.Organization) ||
    !ownerType;

  const isRoleInvalid =
    !role ||
    (role !== APITypes.ManagerRolesType.SuperAdmin &&
      role !== APITypes.ManagerRolesType.Admin &&
      role !== APITypes.ManagerRolesType.Manager &&
      role !== APITypes.ManagerRolesType.Accountant &&
      role !== APITypes.ManagerRolesType.MaintenanceStaff);

  let userInfo = await getUserInfoWithLambda({ userID: userID });
  let retries = 0;
  const maxRetries = 3;

  while (!userInfo.success && retries < maxRetries) {
    await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, retries)));
    userInfo = await getUserInfoWithLambda({ userID: userID });
    retries++;
  }

  if (!userInfo.success) {
    if (ownerID && !isOwnerTypeInvalid && role && !isRoleInvalid) {
      const fallbackResult = {
        success: true as const,
        data: {
          ownerID,
          ownerType,
          role,
        },
        profile: null,
        didFetchUserLoader: false,
      };
      setAuthContextCache(userID, fallbackResult);
      return fallbackResult;
    }

    const failureResult = {
      success: false as const,
      error: userInfo.error,
      data: {
        ownerID: userID || "",
        ownerType,
        role,
      },
      profile: null,
      didFetchUserLoader: true,
    };
    invalidateAuthContextCache();
    return failureResult;
  }

  if (userInfo.data.organizationID) {
    ownerID = userInfo.data.organizationID;
    ownerType = APITypes.OwnerType.Organization;
    role = userInfo.data.roles || APITypes.ManagerRolesType.MaintenanceStaff;
  } else {
    ownerID = userInfo.data.id;
    ownerType = APITypes.OwnerType.User;
    role = APITypes.ManagerRolesType.SuperAdmin;
  }

  const cookieOpts = {
    secure: typeof window !== "undefined" ? window.location?.protocol === "https:" : true,
    sameSite: "lax" as const,
    expires: 30,
  };
  Cookies.set("ownerID", ownerID, cookieOpts);
  Cookies.set("ownerType", ownerType, cookieOpts);
  Cookies.set("role", role, cookieOpts);

  const refreshedResult = {
    success: true as const,
    data: {
      ownerID: ownerID || "",
      ownerType,
      role,
    },
    profile: userInfo.profile,
    didFetchUserLoader: true,
  };
  setAuthContextCache(userID, refreshedResult);
  return refreshedResult;
};

export const clearCookiesData = () => {
  Cookies.remove("ownerID");
  Cookies.remove("ownerType");
  Cookies.remove("role");
  invalidateAuthContextCache();
  clearCachedIdToken();
};
